import React from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import Picture from './Picture';
import LoopVideo from './LoopVideo';
import FilmPlayer from './FilmPlayer';
import { srcSet, toMediaLang, type MediaLang } from './media';
import { DEMO_URL, CONTACT_EMAIL, v2Path, useDocumentTitle } from './links';
import { useEditionKey, useSite, type Edition } from './edition';

interface Step {
  title: string;
  body: string;
}

interface Member {
  name: string;
  role: string;
  body: string;
}

/**
 * The team section's photo per edition. The founders' photo shows Antonio, so the
 * `ceo` edition shows Nicole's own portrait instead (HOF-662).
 */
const TEAM_PHOTOS: Record<Edition, { name: string; widths: number[]; width: number; height: number }> = {
  founders: { name: 'team-founders-wide', widths: [414, 827], width: 827, height: 661 },
  ceo: { name: 'team-nicole', widths: [300, 600], width: 600, height: 600 },
};

const DemoLink: React.FC<{ label: string; className?: string }> = ({ label, className = '' }) => (
  <a className={`hv2-button hv2-button--primary ${className}`} href={DEMO_URL} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
);

/** Hero photo with the 4:5 phone crop below 700 px; the push-in loop plays on wider screens only. */
const HeroMedia: React.FC<{ alt: string; lang: MediaLang }> = ({ alt, lang }) => (
  <div className="hv2-hero__media">
    <picture>
      <source media="(max-width: 700px)" type="image/avif" srcSet={srcSet('hero-dawn-m', [640, 960, 1280], 'avif')} />
      <source media="(max-width: 700px)" type="image/webp" srcSet={srcSet('hero-dawn-m', [640, 960, 1280], 'webp')} />
      <source media="(max-width: 700px)" srcSet={srcSet('hero-dawn-m', [640, 960, 1280], 'jpg')} />
      <source type="image/avif" srcSet={srcSet('hero-dawn', [960, 1280, 1920, 2560], 'avif')} />
      <source type="image/webp" srcSet={srcSet('hero-dawn', [960, 1280, 1920, 2560], 'webp')} />
      <img
        src="/media/hero-dawn-1920.jpg"
        srcSet={srcSet('hero-dawn', [960, 1280, 1920, 2560], 'jpg')}
        sizes="100vw"
        alt={alt}
        width={1920}
        height={1080}
        fetchPriority="high"
        decoding="async"
      />
    </picture>
    <LoopVideo name="hero-dawn-loop" alt="" lang={lang} className="hv2-hero__loop" />
  </div>
);

const HomeV2Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = toMediaLang(i18n.language);
  const { edition, base } = useSite();
  const ek = useEditionKey();
  const teamPhoto = TEAM_PHOTOS[edition];
  useDocumentTitle(t('homeV2.meta.title'));

  const recognition = t('homeV2.recognition.items', { returnObjects: true }) as string[];
  const questions = t('homeV2.values.earn.questions', { returnObjects: true }) as string[];
  const steps = t('homeV2.how.steps', { returnObjects: true }) as Step[];
  const members = t(ek('team.members'), { returnObjects: true }) as Member[];
  const sample = t('homeV2.how.sample');

  const screens = [
    { name: 'product-liquiditaetsplanung', widths: [1000, 2000], width: 2000, height: 1098, caption: t('homeV2.how.screens.liquidity') },
    { name: 'product-deckungsbeitrag', widths: [747, 1494], width: 1494, height: 1406, caption: t('homeV2.how.screens.margin') },
    { name: `earn-tablet-${lang}-still`, widths: [960, 1920], width: 1920, height: 1080, caption: t('homeV2.how.screens.channels') },
    { name: `save-tablet-${lang}-still`, widths: [960, 1920], width: 1920, height: 1080, caption: t('homeV2.how.screens.labour') },
    { name: `risk-phone-${lang}-still`, widths: [960, 1920], width: 1920, height: 1080, caption: t('homeV2.how.screens.alert') },
  ];

  return (
    <div className="hv2-home">
      <section className="hv2-hero hv2-dark" data-testid="home-v2-hero">
        <HeroMedia alt={t('homeV2.hero.imageAlt')} lang={lang} />
        <div className="hv2-hero__inner">
          <p className="hv2-eyebrow">{t('homeV2.hero.eyebrow')}</p>
          <h1 className="hv2-hero__title">{t('homeV2.hero.title')}</h1>
          <p className="hv2-hero__lead" data-testid="home-v2-hero-lead">
            {t('homeV2.hero.lead')}
          </p>
          <div className="hv2-hero__actions">
            <DemoLink label={t('homeV2.hero.ctaDemo')} />
            <a className="hv2-button hv2-button--ghost" href="#film">
              {t('homeV2.hero.ctaFilm')}
            </a>
          </div>
        </div>
      </section>

      <section className="hv2-recognition hv2-dark" data-testid="home-v2-recognition" aria-label={t('homeV2.recognition.label')}>
        <ul>
          {recognition.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {/* No heading or caption here: the film opens and closes on its own lines. */}
      <section
        className="hv2-section hv2-film-section"
        id="film"
        data-testid="home-v2-film"
        aria-label={t('homeV2.film.sectionLabel')}
      >
        <FilmPlayer
          key={lang}
          film={`film-${lang}`}
          poster={`film-${lang}-poster`}
          captions={[{ src: `film-${lang}.vtt`, srclang: lang, label: t('homeV2.film.captions'), default: true }]}
          title={t('homeV2.film.play')}
          lang={lang}
        />
        <p className="hv2-film__note">{t('homeV2.film.aiNote')}</p>
      </section>

      <section className="hv2-band hv2-band--earn hv2-dark" data-testid="home-v2-value-earn">
        <div className="hv2-band__backdrop" aria-hidden="true">
          <Picture name="earn-punnets" widths={[960, 1280, 1920]} alt="" width={1920} height={1080} />
        </div>
        <div className="hv2-band__inner">
          <div className="hv2-band__text">
            <span className="hv2-band__num">01</span>
            <p className="hv2-band__kicker">{t('homeV2.values.earn.kicker')}</p>
            <h2>{t('homeV2.values.earn.title')}</h2>
            <p>{t('homeV2.values.earn.body')}</p>
            <ul className="hv2-chips" aria-label={t('homeV2.values.earn.questionsLabel')}>
              {questions.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
          </div>
          <div className="hv2-band__media">
            <LoopVideo name="earn-tablet" localized hasStill alt={t('homeV2.values.earn.mediaAlt')} lang={lang} className="hv2-frame" />
          </div>
        </div>
      </section>

      <section className="hv2-band hv2-band--save hv2-band--reverse" data-testid="home-v2-value-save">
        <div className="hv2-band__inner">
          <div className="hv2-band__text">
            <span className="hv2-band__num">02</span>
            <p className="hv2-band__kicker">{t('homeV2.values.save.kicker')}</p>
            <h2>{t('homeV2.values.save.title')}</h2>
            <p>{t('homeV2.values.save.body')}</p>
          </div>
          <div className="hv2-band__media hv2-band__media--stack">
            <LoopVideo name="save-packing" alt={t('homeV2.values.save.packingAlt')} lang={lang} className="hv2-frame" />
            <LoopVideo
              name="save-tablet"
              localized
              hasStill
              alt={t('homeV2.values.save.mediaAlt')}
              lang={lang}
              className="hv2-frame hv2-frame--inset"
            />
          </div>
        </div>
      </section>

      <section className="hv2-band hv2-band--risk hv2-band--wide" data-testid="home-v2-value-risk">
        <div className="hv2-band__inner">
          <div className="hv2-band__text">
            <span className="hv2-band__num">03</span>
            <p className="hv2-band__kicker">{t('homeV2.values.risk.kicker')}</p>
            <h2>{t('homeV2.values.risk.title')}</h2>
            <p>{t('homeV2.values.risk.body')}</p>
          </div>
          <div className="hv2-band__media hv2-band__media--screen">
            <Picture
              className="hv2-screen"
              name="product-liquiditaetsplanung"
              widths={[1000, 2000]}
              alt={t('homeV2.values.risk.screenAlt')}
              width={2000}
              height={1098}
              sizes="(max-width: 1280px) 100vw, 1280px"
            />
            <LoopVideo
              name="risk-phone"
              localized
              hasStill
              alt={t('homeV2.values.risk.phoneAlt')}
              lang={lang}
              className="hv2-frame hv2-frame--phone"
            />
          </div>
        </div>
      </section>

      <section className="hv2-section hv2-how" id="so-funktionierts" data-testid="home-v2-how">
        <h2>{t('homeV2.how.title')}</h2>
        <ol className="hv2-steps">
          {steps.map((step, i) => (
            <li key={step.title}>
              <span className="hv2-steps__num">{i + 1}</span>
              <strong>{step.title}</strong> {step.body}
            </li>
          ))}
        </ol>
        <div className="hv2-gallery" role="list" aria-label={t('homeV2.how.galleryLabel')}>
          {screens.map((s) => (
            <figure className="hv2-gallery__item" role="listitem" key={s.name}>
              <div className="hv2-gallery__shot">
                <Picture name={s.name} widths={s.widths} alt={s.caption} width={s.width} height={s.height} sizes="(max-width: 700px) 85vw, 420px" />
                <span className="hv2-badge">{sample}</span>
              </div>
              <figcaption>{s.caption}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section
        className={`hv2-section hv2-team${edition === 'ceo' ? ' hv2-team--portrait' : ''}`}
        data-testid="home-v2-team"
      >
        <div className="hv2-team__photo">
          <Picture
            name={teamPhoto.name}
            widths={teamPhoto.widths}
            alt={t(ek('team.photoAlt'))}
            width={teamPhoto.width}
            height={teamPhoto.height}
            sizes={edition === 'ceo' ? '(max-width: 860px) 100vw, 360px' : '(max-width: 860px) 100vw, 520px'}
          />
        </div>
        <div className="hv2-team__text">
          <h2>{t(ek('team.title'))}</h2>
          {members.map((m) => (
            <div className="hv2-person" key={m.name}>
              <p className="hv2-person__name">{m.name}</p>
              <p className="hv2-person__role">{m.role}</p>
              <p>{m.body}</p>
            </div>
          ))}
          <div className="hv2-team__links">
            <Link className="hv2-link" to={v2Path('company', lang, base)}>
              {t('homeV2.team.more')} →
            </Link>
            <Link className="hv2-link" to={`${v2Path('company', lang, base)}#pitch`}>
              {t('homeV2.team.pitch')}
            </Link>
          </div>
        </div>
      </section>

      <section className="hv2-closing hv2-dark" data-testid="home-v2-closing">
        <div className="hv2-closing__backdrop" aria-hidden="true">
          <Picture name="closing-sunset" widths={[960, 1280, 1920, 2560]} alt="" width={1920} height={1080} />
        </div>
        <div className="hv2-closing__inner">
          <h2>{t('homeV2.closing.title')}</h2>
          <p>{t('homeV2.closing.sub')}</p>
          <DemoLink label={t('homeV2.closing.cta')} className="hv2-button--large" />
          <p className="hv2-closing__mail">
            {t('homeV2.closing.emailLabel')} <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomeV2Page;
